/**
 * RecipeVersionsModal — admin-only history viewer + diff + revert.
 *
 * Shows every snapshot of the recipe (stored in recipe_versions when PUT
 * /recipes/{id} fires). Clicking a version expands a row-level diff vs the
 * CURRENT recipe (ingredients added / removed / qty changed). Admin can
 * Revert the recipe to any snapshot (which itself is snapshotted first so
 * the revert is also reversible).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, RotateCcw, ChevronDown, ChevronRight, History as HistoryIcon, Plus, Minus, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RecipeVersionsModal({ recipe, recipeToken, onClose, onReverted }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [reverting, setReverting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/production/recipes/${recipe.id || recipe.product_id}/versions`, { withCredentials: true });
      setVersions(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load versions');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = (id) => setExpanded((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const revert = async (v) => {
    if (!window.confirm(`Revert "${recipe.product_name}" to the version from ${new Date(v.snapshot_at).toLocaleString('en-IN')}?\n\nThe current state will be auto-snapshotted first, so you can always come back.`)) return;
    setReverting(v.id);
    try {
      await axios.post(
        `${API}/production/recipes/${recipe.id || recipe.product_id}/revert/${v.id}`,
        {},
        { withCredentials: true, headers: { 'X-Recipe-Token': recipeToken || '' } }
      );
      toast.success('Recipe reverted');
      onReverted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revert failed');
    } finally { setReverting(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="recipe-versions-modal">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
              <HistoryIcon size={14} className="text-amber-400" /> Version History
            </h3>
            <p className="text-zinc-500 text-xs mt-0.5">{recipe.product_name} · {versions.length} saved version{versions.length === 1 ? '' : 's'}</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading && <div className="text-zinc-400 text-sm py-6 text-center">Loading history…</div>}
          {!loading && versions.length === 0 && (
            <div className="text-zinc-500 text-sm py-8 text-center">
              No prior versions yet. Every time you save an edit, a snapshot is stored here.
            </div>
          )}
          {versions.map((v) => (
            <VersionRow
              key={v.id}
              version={v}
              current={recipe}
              expanded={expanded.has(v.id)}
              onToggle={() => toggle(v.id)}
              onRevert={() => revert(v)}
              reverting={reverting === v.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function VersionRow({ version, current, expanded, onToggle, onRevert, reverting }) {
  const snap = version.snapshot || {};
  const diff = useMemo(() => computeDiff(snap.materials || [], current.materials || []), [snap.materials, current.materials]);
  const when = new Date(version.snapshot_at).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 overflow-hidden" data-testid={`version-${version.id}`}>
      <div className="px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-800/40 cursor-pointer" onClick={onToggle}>
        <button type="button" className="text-zinc-500" data-testid={`version-toggle-${version.id}`}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-zinc-100 text-xs font-semibold">{when}</div>
          <div className="text-zinc-500 text-[11px] mt-0.5 flex items-center gap-2 flex-wrap">
            <span>by {version.snapshot_by || 'unknown'}</span>
            <span className="text-zinc-600">·</span>
            <span>{(snap.materials || []).length} ingredients</span>
            <span className="text-zinc-600">·</span>
            <span className="font-mono">{snap.materials_total_kg || snap.batch_size_kg || 0} kg batch</span>
            {version.note && <>
              <span className="text-zinc-600">·</span>
              <span className="italic text-zinc-400">{version.note}</span>
            </>}
          </div>
        </div>
        <DiffPill diff={diff} />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRevert(); }}
          disabled={reverting}
          className="text-xs px-2.5 py-1 rounded-lg bg-amber-500/15 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 flex items-center gap-1.5 disabled:opacity-50"
          data-testid={`version-revert-${version.id}`}
        >
          <RotateCcw size={11} /> {reverting ? 'Reverting…' : 'Revert'}
        </button>
      </div>
      {expanded && (
        <div className="border-t border-zinc-800 p-3 bg-zinc-950/60">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Diff vs current recipe</div>
          <DiffTable diff={diff} />
          {snap.notes && snap.notes !== current.notes && (
            <div className="mt-3 text-[11px] text-zinc-400">
              <span className="text-zinc-500">Notes changed:</span>
              <div className="mt-1 p-2 rounded bg-zinc-900 border border-zinc-800 font-mono text-zinc-300 whitespace-pre-wrap">{snap.notes || '—'}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DiffPill({ diff }) {
  const adds = diff.added.length;
  const rems = diff.removed.length;
  const chgs = diff.changed.length;
  if (!adds && !rems && !chgs) return <span className="text-[10px] text-zinc-600">no changes</span>;
  return (
    <div className="flex items-center gap-1 text-[10px] font-mono">
      {adds > 0 && <span className="px-1.5 py-0.5 rounded bg-green-900/30 text-green-300 border border-green-700/40">+{adds}</span>}
      {rems > 0 && <span className="px-1.5 py-0.5 rounded bg-red-900/30 text-red-300 border border-red-700/40">−{rems}</span>}
      {chgs > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40">~{chgs}</span>}
    </div>
  );
}

function DiffTable({ diff }) {
  const rows = [
    ...diff.removed.map((m) => ({ type: 'removed', name: m.material_name, from: m.quantity_per_batch, to: 0, unit: m.unit })),
    ...diff.added.map((m) => ({ type: 'added', name: m.material_name, from: 0, to: m.quantity_per_batch, unit: m.unit })),
    ...diff.changed.map((m) => ({ type: 'changed', name: m.material_name, from: m.from, to: m.to, unit: m.unit })),
    ...diff.unchanged.map((m) => ({ type: 'same', name: m.material_name, from: m.quantity_per_batch, to: m.quantity_per_batch, unit: m.unit })),
  ];
  if (rows.length === 0) return <div className="text-zinc-500 text-xs">No ingredients in either version.</div>;
  return (
    <table className="w-full text-xs">
      <thead className="text-zinc-500 text-[10px] uppercase">
        <tr>
          <th className="text-left px-2 py-1 w-8"></th>
          <th className="text-left px-2 py-1">Ingredient</th>
          <th className="text-right px-2 py-1">This Version</th>
          <th className="text-center px-2 py-1 w-8"></th>
          <th className="text-right px-2 py-1">Current</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-zinc-800">
        {rows.map((r, i) => (
          <tr key={i} className={
            r.type === 'removed' ? 'bg-red-900/10' :
            r.type === 'added' ? 'bg-green-900/10' :
            r.type === 'changed' ? 'bg-amber-900/10' : ''
          }>
            <td className="px-2 py-1.5">
              {r.type === 'removed' ? <Minus size={10} className="text-red-400" />
                : r.type === 'added' ? <Plus size={10} className="text-green-400" />
                : r.type === 'changed' ? <ArrowRight size={10} className="text-amber-400" />
                : null}
            </td>
            <td className="px-2 py-1.5 text-zinc-200">{r.name}</td>
            <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{r.from || '—'} {r.unit || ''}</td>
            <td className="px-2 py-1.5 text-center text-zinc-600">→</td>
            <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{r.to || '—'} {r.unit || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Diff helper — compares two materials arrays keyed by material_name.
function computeDiff(fromList, toList) {
  const norm = (x) => (x || '').toLowerCase().trim();
  const fromMap = {}; fromList.forEach((m) => { if (m.material_name) fromMap[norm(m.material_name)] = m; });
  const toMap = {}; toList.forEach((m) => { if (m.material_name) toMap[norm(m.material_name)] = m; });
  const added = [];
  const removed = [];
  const changed = [];
  const unchanged = [];
  Object.keys(toMap).forEach((k) => { if (!fromMap[k]) added.push(toMap[k]); });
  Object.keys(fromMap).forEach((k) => { if (!toMap[k]) removed.push(fromMap[k]); });
  Object.keys(fromMap).forEach((k) => {
    if (!toMap[k]) return;
    const a = parseFloat(fromMap[k].quantity_per_batch) || 0;
    const b = parseFloat(toMap[k].quantity_per_batch) || 0;
    if (Math.abs(a - b) > 0.0001) {
      changed.push({ material_name: toMap[k].material_name, from: a, to: b, unit: toMap[k].unit || fromMap[k].unit });
    } else {
      unchanged.push(toMap[k]);
    }
  });
  return { added, removed, changed, unchanged };
}
